import { ReactNode } from 'react';

interface CategoryPageLayoutProps {
  title: string;
  subtitle?: string;
  description?: string;
  lastUpdated?: string;
  icon?: ReactNode;
  breadcrumbs?: { label: string; href: string }[];
  heroVariant?: 'default' | 'editorial';
  heroRightElement?: ReactNode;
  children: ReactNode;
}

export default function CategoryPageLayout({
  title,
  subtitle,
  description,
  lastUpdated,
  icon,
  breadcrumbs,
  heroVariant = 'default',
  heroRightElement,
  children
}: CategoryPageLayoutProps) {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Banner */}
      <div
        className={`text-white ${heroVariant === 'editorial'
          ? 'bg-gradient-to-b from-purple-800 via-purple-700 to-purple-700 py-10 md:py-12'
          : 'bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 py-16'
          }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          {/* Breadcrumbs */}
          {breadcrumbs && breadcrumbs.length > 0 && (
            <div
              className={`flex items-center gap-2 text-sm ${
                heroVariant === 'editorial' ? 'text-purple-100/90 mb-5' : 'text-purple-200 mb-4'
              }`}
            >
              {breadcrumbs.map((crumb, index) => (
                <div key={index} className="flex items-center gap-2">
                  <a href={crumb.href} className="hover:text-white transition-colors">
                    {crumb.label}
                  </a>
                  {index < breadcrumbs.length - 1 && (
                    <i className="ri-arrow-right-s-line text-xs"></i>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Title Section */}
          {heroVariant === 'editorial' ? (
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
              <div className="max-w-7xl flex-1 min-w-0">
                <h1 className="text-3xl md:text-5xl font-extrabold leading-tight mb-5">{title}</h1>
                {(subtitle || lastUpdated) && (
                  <div className="flex flex-wrap items-center gap-3 text-purple-100 mb-5">
                    {subtitle && (
                      <span className="inline-flex items-center gap-2 text-base font-semibold">
                        <i className="ri-verified-badge-fill text-emerald-300"></i>
                        {subtitle}
                      </span>
                    )}
                    {lastUpdated && (
                      <span className="inline-flex items-center gap-2 text-sm text-purple-200">
                        <i className="ri-time-line"></i>
                        Last updated: {lastUpdated}
                      </span>
                    )}
                  </div>
                )}
                {description && (
                  <p className="text-purple-100/95 text-base leading-relaxed max-w-4xl">
                    {description}
                  </p>
                )}
              </div>
              {heroRightElement && (
                <div className="hidden lg:flex w-[200px] xl:w-[240px] 2xl:w-[280px] flex-shrink-0 lg:self-end items-end justify-end">
                  {heroRightElement}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-start gap-4">
              {icon && (
                <div className="w-16 h-16 flex items-center justify-center bg-white/10 rounded-xl backdrop-blur-sm">
                  {icon}
                </div>
              )}
              <div className="flex-1">
                <h1 className="text-4xl font-bold mb-3">{title}</h1>
                {subtitle && (
                  <p className="text-xl text-purple-100 mb-4">{subtitle}</p>
                )}
                {description && (
                  <p className="text-purple-200 text-base leading-relaxed max-w-3xl">
                    {description}
                  </p>
                )}
                {lastUpdated && (
                  <div className="flex items-center gap-2 mt-4 text-sm text-purple-300">
                    <i className="ri-time-line"></i>
                    <span>Last updated: {lastUpdated}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {children}
      </div>
    </div>
  );
}